import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Card, Alert, Loader, Badge, Table, Dialog } from '@dreamtree-org/twreact-ui';
import { DatabaseZap, Play, PlayCircle, CheckCircle2, RefreshCw, GitCompareArrows, Trash2, Eraser } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { storageKey } from '@/lib/storageKey';
import TablePicker from './TablePicker';

// The DDL "verb" a step performs. Mapped onto the five Badge variants the design
// system ships — destructive verbs read as error, widening ones as info.
const TYPE_VARIANT = {
  create_table: 'success',
  add_column: 'primary',
  modify_column: 'warning',
  create_index: 'primary',
  drop_index: 'error',
  add_foreign_key: 'primary',
  drop_foreign_key: 'error',
};

const STATUS_VARIANT = {
  pending: 'default',
  running: 'primary',
  success: 'success',
  failed: 'error',
  skipped: 'default',
};

const RUN_STATUS_VARIANT = {
  planned: 'default',
  running: 'primary',
  completed: 'success',
  partial: 'warning',
  failed: 'error',
};

// Both migration screens persist their connection URLs so a long session doesn't mean
// re-pasting credentials. Namespaced via storageKey — localStorage is shared per
// host+port, and the un-namespaced `dbmig:*` keys are exactly the collision that
// helper exists to prevent.
const SRC_KEY = storageKey('dbmig:sourceUrl');
const TGT_KEY = storageKey('dbmig:targetUrl');

const RUNNABLE = ['pending', 'failed'];
const errText = (ex, fallback) => (ex.response && ex.response.data && ex.response.data.error) || ex.message || fallback;

/**
 * Utility Tools › DB Migration.
 *
 * Compare a SOURCE database (the reference schema) against a TARGET, list the DDL needed
 * to bring the target in line, and apply it one step at a time with a server-side audit
 * trail. The server enforces strict sequence — this screen only drives it.
 */
export default function DbMigration() {
  const { fetchApi } = useAppApi();

  const [sourceUrl, setSourceUrl] = useState(() => localStorage.getItem(SRC_KEY) || '');
  const [targetUrl, setTargetUrl] = useState(() => localStorage.getItem(TGT_KEY) || '');
  const [tableScope, setTableScope] = useState({ allMode: true, tables: [] });

  const [planning, setPlanning] = useState(false);
  const [run, setRun] = useState(null);
  const [steps, setSteps] = useState([]);
  const [executingId, setExecutingId] = useState(null);
  const [runningAll, setRunningAll] = useState(false);
  const [expanded, setExpanded] = useState({});

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');

  // Empty string is a real value here — "Clear saved" must actually forget them.
  useEffect(() => { localStorage.setItem(SRC_KEY, sourceUrl); }, [sourceUrl]);
  useEffect(() => { localStorage.setItem(TGT_KEY, targetUrl); }, [targetUrl]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await fetchApi('dbMigrationRuns');
      setHistory((res && res.data) || []);
    } catch {
      // Non-fatal: history is a convenience, not part of the migration flow.
    } finally {
      setLoadingHistory(false);
    }
  }, [fetchApi]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // The lowest-seq step still needing a run — the only one whose button is enabled,
  // mirroring the server's strict-sequence rule.
  const nextStepId = useMemo(() => {
    const next = steps.find((s) => RUNNABLE.includes(s.status));
    return next ? next.id : null;
  }, [steps]);

  const hasRunnable = useMemo(() => steps.some((s) => RUNNABLE.includes(s.status)), [steps]);

  const compare = async () => {
    setErr('');
    setNotice('');
    if (!sourceUrl.trim() || !targetUrl.trim()) {
      setErr('Enter both the source and target connection URLs.');
      return;
    }
    if (!tableScope.allMode && tableScope.tables.length === 0) {
      setErr('Pick at least one table, or choose All tables.');
      return;
    }
    setPlanning(true);
    setRun(null);
    setSteps([]);
    try {
      const res = await fetchApi('dbMigrationPlan', {
        sourceUrl: sourceUrl.trim(),
        targetUrl: targetUrl.trim(),
        ...(tableScope.allMode ? {} : { tables: tableScope.tables }),
      });
      setRun(res.run);
      setSteps(res.steps || []);
      setNotice(res.steps?.length
        ? `${res.steps.length} migration step(s) found.`
        : 'Target already matches source — nothing to migrate.');
      loadHistory();
    } catch (ex) {
      setErr(errText(ex, 'Could not compare schemas'));
    } finally {
      setPlanning(false);
    }
  };

  const runStep = async (step) => {
    if (!run) return;
    setErr('');
    setNotice('');
    setExecutingId(step.id);
    try {
      const res = await fetchApi('dbMigrationExecuteStep', { runId: run.id, stepId: step.id });
      setRun(res.run);
      setSteps(res.steps || []);
      if (res.lastStep?.ok) setNotice(`Step #${step.seq} (${step.type}) applied.`);
      else setErr(res.lastStep?.error || 'Statement failed.');
      loadHistory();
    } catch (ex) {
      setErr(errText(ex, 'Could not run step'));
    } finally {
      setExecutingId(null);
    }
  };

  /**
   * Apply every remaining step in sequence, stopping at the first failure. Driven off
   * the step list the server returns each iteration rather than React state — state
   * updates are async and would still hold the previous list on the next pass.
   */
  const runAll = async () => {
    if (!run || runningAll) return;
    setErr('');
    setNotice('');
    setRunningAll(true);
    let current = steps;
    try {
      for (;;) {
        const next = current.find((s) => RUNNABLE.includes(s.status));
        if (!next) break;
        setExecutingId(next.id);
        let res;
        try {
          res = await fetchApi('dbMigrationExecuteStep', { runId: run.id, stepId: next.id });
        } catch (ex) {
          setErr(errText(ex, 'Could not run step'));
          break;
        }
        setRun(res.run);
        setSteps(res.steps || []);
        current = res.steps || [];
        if (!res.lastStep?.ok) {
          setErr(`Step #${next.seq} failed — fix it, then Run All again. ${res.lastStep?.error || ''}`.trim());
          break;
        }
      }
      if (current.length && !current.some((s) => RUNNABLE.includes(s.status))) setNotice('Migration complete — all steps applied.');
      loadHistory();
    } finally {
      setExecutingId(null);
      setRunningAll(false);
    }
  };

  const clearHistory = async () => {
    setClearing(true);
    try {
      await fetchApi('dbMigrationClear');
      setHistory([]);
      setNotice('Migration history cleared.');
      setConfirmClear(false);
    } catch (ex) {
      setErr(errText(ex, 'Could not clear history'));
    } finally {
      setClearing(false);
    }
  };

  const openRun = async (id) => {
    setErr('');
    try {
      const res = await fetchApi('dbMigrationRun', { id });
      setRun(res.run);
      setSteps(res.steps || []);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (ex) {
      setErr(errText(ex, 'Could not load run'));
    }
  };

  const progressPct = run && run.total_steps > 0 ? Math.round((run.executed_steps / run.total_steps) * 100) : 0;

  const stepColumns = [
    { key: 'seq', label: '#', render: (s) => <span className="font-mono opacity-50">{s.seq}</span> },
    { key: 'type', label: 'Type', render: (s) => <Badge variant={TYPE_VARIANT[s.type] || 'default'}>{s.type}</Badge> },
    {
      key: 'table_name',
      label: 'Table',
      render: (s) => (
        <div>
          <div className="font-medium">{s.table_name}</div>
          <div className="text-[11px] opacity-50">{s.object_name}</div>
        </div>
      ),
    },
    {
      key: 'sql_text',
      label: 'SQL',
      render: (s) => (
        <div className="max-w-md">
          <button
            type="button"
            title="Click to expand"
            onClick={() => setExpanded((p) => ({ ...p, [s.id]: !p[s.id] }))}
            className={`block w-full text-left text-[11px] font-mono bg-secondary-50 dark:bg-secondary-800 rounded px-2 py-1 ${expanded[s.id] ? 'whitespace-pre-wrap' : 'truncate'}`}
          >
            {s.sql_text}
          </button>
          {s.status === 'failed' && s.error_message ? (
            <div className="text-[11px] text-error-600 mt-1">{s.error_message}</div>
          ) : null}
        </div>
      ),
    },
    { key: 'status', label: 'Status', render: (s) => <Badge variant={STATUS_VARIANT[s.status] || 'default'}>{s.status}</Badge> },
    {
      key: 'action',
      label: '',
      render: (s) =>
        s.status === 'success' ? (
          <CheckCircle2 className="text-success-600" size={18} />
        ) : (
          <Button
            size="sm"
            variant={s.id === nextStepId ? 'primary' : 'secondary'}
            disabled={s.id !== nextStepId || !!executingId}
            loading={executingId === s.id}
            onClick={() => runStep(s)}
            leftIcon={<Play size={14} />}
          >
            {s.status === 'failed' ? 'Retry' : 'Run'}
          </Button>
        ),
    },
  ];

  const historyColumns = [
    {
      key: 'id',
      label: 'Run',
      render: (h) => (
        <button type="button" className="text-primary-600 font-medium hover:underline" onClick={() => openRun(h.id)}>
          #{h.id}
        </button>
      ),
    },
    { key: 'status', label: 'Status', render: (h) => <Badge variant={RUN_STATUS_VARIANT[h.status] || 'default'}>{h.status}</Badge> },
    {
      key: 'source_label',
      label: 'Source → Target',
      render: (h) => <span className="font-mono text-xs break-all">{h.source_label} → {h.target_label}</span>,
    },
    { key: 'steps', label: 'Steps', render: (h) => `${h.executed_steps}/${h.total_steps}` },
    { key: 'created_by_name', label: 'By' },
    { key: 'created_at', label: 'Created' },
  ];

  // Extracted from the JSX: a three-way choice would otherwise be a nested ternary,
  // which this repo's lint config rejects.
  let historyPanel = <Table data={history} columns={historyColumns} pagination withAction={false} />;
  if (loadingHistory && history.length === 0) historyPanel = <Loader text="Loading history…" />;
  else if (history.length === 0) historyPanel = <p className="text-sm opacity-50">No migrations run yet.</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <DatabaseZap size={22} className="text-primary-600" />
        <div>
          <h1 className="text-xl font-bold">DB Migration</h1>
          <p className="text-sm opacity-70">
            Compare a source database against a target and apply the schema changes step by step.
            The source is the reference; only the target is written to.
          </p>
        </div>
      </div>

      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* type=password so a shoulder-surfer or a screen share doesn't expose the
              credentials embedded in the URL. The server stores them encrypted and never
              echoes them back — the run rows only carry a masked label. */}
          <Input
            label="Source DB URL (reference)"
            type="password"
            placeholder="mysql://user:pass@host:3306/source_db"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
          />
          <Input
            label="Target DB URL (to be migrated)"
            type="password"
            placeholder="mysql://user:pass@host:3306/target_db"
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
          />
        </div>

        <div className="md:w-1/2">
          <span className="block text-sm font-medium mb-1">Tables to migrate (from source)</span>
          <TablePicker
            sourceUrl={sourceUrl}
            value={tableScope}
            onChange={setTableScope}
            disabled={planning || runningAll}
            endpoint="dbMigrationTables"
          />
          <p className="text-[11px] opacity-50 mt-1">Defaults to all tables. Pick specific ones to scope the migration.</p>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs opacity-60 max-w-xl">
            MySQL &amp; PostgreSQL. Only additive/alter changes plus index and FK drops are generated —
            never DROP COLUMN or DROP TABLE.
          </p>
          <div className="flex items-center gap-2">
            {sourceUrl || targetUrl ? (
              <Button
                variant="secondary"
                onClick={() => { setSourceUrl(''); setTargetUrl(''); }}
                disabled={planning || runningAll}
                leftIcon={<Eraser size={16} />}
                title="Forget the saved source/target URLs"
              >
                Clear saved
              </Button>
            ) : null}
            <Button onClick={compare} loading={planning} disabled={runningAll} leftIcon={<GitCompareArrows size={16} />}>
              Compare
            </Button>
          </div>
        </div>
      </Card>

      {err ? <Alert variant="error" title="DB Migration">{err}</Alert> : null}
      {notice ? <Alert variant="success" title="DB Migration">{notice}</Alert> : null}

      {run ? (
        <Card className="p-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Badge variant={RUN_STATUS_VARIANT[run.status] || 'default'}>{run.status}</Badge>
              <span className="text-sm font-mono opacity-70 break-all">{run.source_label} → {run.target_label}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">
                {run.executed_steps}/{run.total_steps} done
                {run.failed_steps > 0 ? <span className="text-error-600"> · {run.failed_steps} failed</span> : null}
              </span>
              {hasRunnable ? (
                <Button
                  size="sm"
                  onClick={runAll}
                  loading={runningAll}
                  leftIcon={<PlayCircle size={16} />}
                  title="Run every remaining step in sequence, stopping on the first failure"
                >
                  Run All
                </Button>
              ) : null}
            </div>
          </div>

          <div className="w-full h-2 bg-secondary-100 dark:bg-secondary-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${run.failed_steps > 0 ? 'bg-error-500' : 'bg-success-500'}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>

          {steps.length === 0 ? (
            <div className="flex items-center gap-2 text-success-700 text-sm">
              <CheckCircle2 size={18} /> Schemas already match — nothing to migrate.
            </div>
          ) : (
            <Table data={steps} columns={stepColumns} withAction={false} />
          )}
        </Card>
      ) : null}

      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Recent migrations</h2>
          <div className="flex items-center gap-3">
            {history.length > 0 ? (
              <button type="button" className="flex items-center gap-1 text-xs text-error-600 hover:underline" onClick={() => setConfirmClear(true)}>
                <Trash2 size={14} /> Clear
              </button>
            ) : null}
            <button type="button" className="opacity-50 hover:opacity-100" onClick={loadHistory} title="Refresh">
              <RefreshCw size={15} className={loadingHistory ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
        {historyPanel}
      </Card>

      {confirmClear ? (
        <Dialog isOpen onClose={() => setConfirmClear(false)} title="Clear migration history?">
          <div className="space-y-4">
            <p className="text-sm">
              This deletes the local audit records only. Your source and target databases are not touched.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirmClear(false)}>Cancel</Button>
              <Button variant="destructive" onClick={clearHistory} loading={clearing}>Clear history</Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}
